/* The formula editor's function-hint registry (docs/excel-formula-editor-proposal.md §3.2–3.3):
   fetched once per designer session from api/CanvasTest/FormulaFunctions — the same registry
   both runtimes execute and the save-time lint enforces, so the menu can never drift from the
   backend. Pure logic lives here (unit-tested); FTSFormula.vue owns the DOM. */

let cache = null;

/* Fetch + session cache. `dispatch` is the canvasDesignStore dispatch (keeps this module
   store-agnostic and testable). A failed fetch resolves [] and clears the cache so the next
   editor retries. */
export const loadFormulaFunctions = dispatch => {
  if (!cache) {
    cache = dispatch("formulaFunctions")
      .then(res => (res && res.data && res.data.functions) || [])
      .catch(() => {
        cache = null;
        return [];
      });
  }
  return cache;
};

export const resetFormulaFunctionsCache = () => {
  cache = null;
};

/* The host's visible function set (D5/D8 dedup, applied BEFORE matching):
   - menuHidden entries never surface (chip-taught concepts: TODAY/NOW in both spellings);
   - computed mode keeps only what the DEVICE computes (client tier — which now truthfully
     includes the native sum/hasChange/iif, not just the Excel client tier);
   - a native entry with an excelTwin hides whenever its twin is in the same filtered set —
     so `iif` surfaces in computed mode while `IF` is server-tier, and disappears everywhere
     once Phase 3 flips IF to client. One spelling per concept, per host. */
export const visibleFunctions = (functions, options) => {
  const computedField = options && options.computedField;
  let visible = (functions || []).filter(f => !f.menuHidden);
  if (computedField) visible = visible.filter(f => f.clientTier === "client");
  const names = new Set(visible.map(f => f.name));
  return visible.filter(f => !(f.excelTwin && names.has(f.excelTwin)));
};

/* Case-insensitive prefix match over the visible names (canonical UPPERCASE for the excel
   dialect, lowercase for the native one). Two characters minimum — a single letter matches
   half the registry and would pop a menu on every keystroke. */
export const matchFunctions = (functions, word) => {
  if (!functions || !word || word.length < 2) return [];
  const upper = word.toUpperCase();
  return functions.filter(f => f.name.toUpperCase().indexOf(upper) === 0);
};

/* Field-chip matches for the unified typed menu (D6: fields group first): leaf variables
   matched on display name. Containers (module refs, activity forms) wait for the P3b
   drill-down — offering them without it would be a dead end. */
export const matchFields = (variables, word) => {
  if (!variables || !word || word.length < 2) return [];
  const query = word.toUpperCase();
  return variables.filter(v =>
    v && v.name && !v.moduleId && !v.form
    && v.name.toUpperCase().indexOf(query) === 0);
};

/* D6 ranking: fields first by default; an ALL-UPPERCASE query signals function intent and
   flips the function groups to the top. Groups stay whole — no interleaving. */
export const functionsFirst = word =>
  !!word && word.length >= 2 && word === word.toUpperCase() && /[A-Z]/.test(word);

/* The fields-scope trigger (P3b — one dropdown): a `#` before the caret puts the menu in
   fields scope; everything after it up to the caret is the query (spaces allowed — field
   names carry them). Null = not in fields scope. Same open-string-literal suppression as
   wordAt. */
export const fieldQueryAt = textBeforeCaret => {
  if (textBeforeCaret == null) return null;
  const doubles = (textBeforeCaret.match(/"/g) || []).length;
  const singles = (textBeforeCaret.match(/'/g) || []).length;
  if (doubles % 2 === 1 || singles % 2 === 1) return null;
  const match = /#([^#\n]{0,60})$/.exec(textBeforeCaret);
  return match ? match[1] : null;
};

/* Case-insensitive SUBSTRING filter over a resolved level's variables; an empty query keeps
   the whole level (the "#" just pressed — show everything). Prefix hits rank above mid-name
   hits, each group keeping the level's own order — so "#se" still leads with "Set Number"
   while "#2" can still reach "Variable 2" (SS06026: the tribute menu matched anywhere in the
   name, and a prefix-only filter lost every field named by a trailing number or word). */
export const matchLevel = (items, query) => {
  if (!items) return [];
  if (!query) return items.slice();
  const upper = query.toUpperCase();
  const prefix = [];
  const inner = [];
  for (const v of items) {
    if (!v || !v.name) continue;
    const at = v.name.toUpperCase().indexOf(upper);
    if (at === 0) prefix.push(v);
    else if (at > 0) inner.push(v);
  }
  return prefix.concat(inner);
};

/* Browse view (the menu's empty-query state): registry grouped by category, in the fixed
   Logical / Text / Math / Date order. */
const CATEGORY_ORDER = ["Logical", "Text", "Math", "Date", "Table"];

export const groupByCategory = functions => {
  const groups = [];
  for (const category of CATEGORY_ORDER) {
    const items = (functions || []).filter(f => f.category === category);
    if (items.length) groups.push({ category, items });
  }
  // never drop a function whose category we don't know yet
  const known = new Set(CATEGORY_ORDER);
  const rest = (functions || []).filter(f => !known.has(f.category));
  if (rest.length) groups.push({ category: "Other", items: rest });
  return groups;
};

/* What accepting a suggestion types: the canonical name with parens, caret inside — after
   the closing paren for no-argument functions (today/now), bare for tokens with no call
   shape (rowAutoNumber). D1 ruling: canonical spelling is emitted on ACCEPT only; typed
   text is never silently rewritten. */
export const insertionFor = fn => {
  if (fn.signature.indexOf("(") < 0) {
    return { text: fn.name, caretOffset: fn.name.length };
  }
  const noArgs = /\(\s*\)\s*$/.test(fn.signature);
  return {
    text: fn.name + "()",
    caretOffset: noArgs ? fn.name.length + 2 : fn.name.length + 1
  };
};

/* The word being typed at the caret, given the CHIP-STRIPPED text before it (the caller
   replaces each fts-i-unit with a space — chip labels contain apostrophes that would poison
   the quote heuristic). Returns null when hints must not open:
   - inside a string literal (odd count of unclosed " or ' before the caret);
   - a # word (the tribute chip menu owns the caret);
   - a member access (.Id / .ToString — the honored legacy shapes, not functions);
   - a chip expression ({{…). */
export const wordAt = textBeforeCaret => {
  if (textBeforeCaret == null) return null;
  const doubles = (textBeforeCaret.match(/"/g) || []).length;
  const singles = (textBeforeCaret.match(/'/g) || []).length;
  if (doubles % 2 === 1 || singles % 2 === 1) return null;

  const match = /([A-Za-z][A-Za-z0-9]*)$/.exec(textBeforeCaret);
  if (!match) return null;

  const before = textBeforeCaret.charAt(match.index - 1);
  if (before === "#" || before === "." || before === "{") return null;
  return match[1];
};
